import React from "react";
import { useParams } from "react-router-dom";

import firebase from "firebaseFactory";
import { Page } from "modules/layout/Page";
import { useAuthDataContext, Permissions } from "modules/auth";
import { useEventData } from "modules/events";
import { Heading2 } from "components/Heading2";

import { EventList } from "../events/components/EventList";

import { AggregatedDeviceCard } from "./components/AggregatedDeviceCard";
import { useSiteDataByUnit, useDevicesByUnit } from "./useSiteData";
import { AggregatedDeviceStatsRow } from "./components/AggregatedDeviceStats";
import { AggregatedDeviceStatus } from "./components/AggregatedDeviceStatus";
import { QuickLink } from "./components/QuickLink";

export const UnitDashboard = ({
  getDevicesBySiteId = firebase.function("getDevicesBySiteId"),
  getNow = Date.now,
}) => {
  const { siteId, unitId } = useParams();
  const { enforcer } = useAuthDataContext();
  const { devicesByUnit, isLoading } = useDevicesByUnit(
    siteId,
    getDevicesBySiteId
  );
  const { latestDataByUnit } = useSiteDataByUnit(siteId);
  const { eventData, isLoading: isEventDataLoading } = useEventData(
    siteId,
    unitId,
    undefined,
    3
  );

  const devices = devicesByUnit[unitId] || [];
  const deviceData = Object.values(latestDataByUnit[unitId] || {});

  return (
    <Page
      isLoading={isLoading}
      titleSupplement={
        <AggregatedDeviceStatus
          devices={devices}
          deviceData={deviceData}
          getNow={getNow}
        />
      }
    >
      <div className="mb-6">
        <Heading2>Unit Summary</Heading2>
        <AggregatedDeviceStatsRow
          deviceData={deviceData}
          getNow={getNow}
          isUnit={true}
        />
      </div>
      <div className="mb-6">
        {enforcer.can(Permissions.Unit_ViewEvents, { siteId, unitId }) && (
          <>
            <Heading2>Events</Heading2>
            <EventList
              eventData={eventData}
              isLoading={isEventDataLoading}
              linkMoreTo={`/sites/${siteId}/units/${unitId}/events`}
            />
          </>
        )}
      </div>
      <Heading2 className="mt-6">Devices in {unitId}</Heading2>
      <div className="flex flex-wrap -mx-2">
        {(devicesByUnit[unitId] || []).map((device, index) => (
          <AggregatedDeviceCard
            linkTo={`/sites/${siteId}/units/${unitId}/device/${device.deviceId}`}
            key={index}
            title={device.deviceId}
            devices={[device]}
            deviceData={[latestDataByUnit[unitId]?.[device.deviceId]].filter(
              (x) => x !== undefined
            )}
            getNow={getNow}
            quickLinks={
              <>
                <QuickLink
                  to={`/sites/${siteId}/units/${unitId}/device/${device.deviceId}/command`}
                >
                  Send Command
                </QuickLink>
                <QuickLink
                  to={`/sites/${siteId}/units/${unitId}/device/${device.deviceId}/messages`}
                >
                  Messages
                </QuickLink>
                <QuickLink
                  to={`/sites/${siteId}/units/${unitId}/device/${device.deviceId}/logs`}
                >
                  Logs
                </QuickLink>
                <QuickLink
                  to={`/sites/${siteId}/units/${unitId}/device/${device.deviceId}/waveforms`}
                >
                  Waveforms
                </QuickLink>
              </>
            }
          />
        ))}
      </div>
    </Page>
  );
};
